import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Zone-wise TL Statistics Chart
 * Bar chart showing TL statistics across different zones
 */
const MBFZoneStatisticsChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data matching the Figma design
    const defaultData = [
        { zone: 'West', count: 600 },
        { zone: 'North', count: 800 },
        { zone: 'South', count: 1500 },
        { zone: 'Central', count: 2400 },
        { zone: 'East', count: 2100 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.zone || item.label);
    const values = chartData.map(item => item.count || item.value || 0);
    const maxVal = Math.max(...values, 0);
    const suggestedMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;
            const barThickness = isMobile ? 30 : 50;

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'TL Count',
                            data: values,
                            backgroundColor: '#e8b4e8',
                            borderColor: '#d999d9',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: barThickness,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                            callbacks: {
                                label: (context) => `TLs: ${context.raw.toLocaleString()}`,
                            },
                        },
                        datalabels: { display: false },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                        },
                        y: {
                            min: 0,
                            suggestedMax: suggestedMax,
                            ticks: {
                                color: '#666666',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, chartData, labels, values]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Zone-wise TL Statistics
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    <canvas ref={chartRef} />
                </Box>
            </Box>
        </Box>
    );
};

export default MBFZoneStatisticsChart;
